import {
  Button,
  Checkbox,
  Input,
  Popconfirm,
  Space,
  Table,
  message,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { useNavigate } from "react-router-dom";
import { EntityForm } from "../../components/EntityForm";
import { useEffect, useMemo, useState } from "react";

import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import {
  getEquipments,
  updateEquipment,
  createEquipment,
  deleteEquipment,
} from "../../api/equipment";
import { UserRoles } from "../user";
import { NoPermission } from "../../components/EntityForm/NoPermision/noPermission";
import { getFileUrl } from "../../api/file";
import { RateBy } from "../../helpers/rate";

export const CompanyEquipmentStatus = {
  Active: "active",
  InActive: "inactive",
  Offline: "offline",
  Online: "online",
  Maintenance: "maintenance",
  Repair: "repair",
  Retired: "retired",
};

export const EquipmentType = {
  Equipment: "equipment",
  Asset: "asset",
  Consumable: "consumable",
};

export function Equipments({ user }) {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const dataIndex = (page - 1) * perPage;

  const [equipments, setEquipments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showCreateEquipments, setShowCreateEquipments] = useState(false);
  const [editEquipments, setEditEquipments] = useState(null);

  const sections = useMemo(() => {
    const attachments = [];
    const avatars = [];
    if (editEquipments) {
      attachments.push(
        ...(editEquipments.attachments || []).map((attachment) => {
          return {
            ...attachment,
            uid: attachment.id,
            name: attachment.originalName,
            status: "done",
            url: getFileUrl(attachment),
          };
        })
      );
      avatars.push(
        ...(editEquipments.avatars || []).map((avatar) => {
          return {
            ...avatar,
            uid: avatar.id,
            name: avatar.originalName,
            status: "done",
            url: getFileUrl(avatar),
          };
        })
      );
    }
    return [
      {
        items: [
          {
            type: "attachments",
            label: "Equipment Images (2MB)",
            name: "avatars",
            listType: "picture-card",
            col: 12,
            maxLen: 200,
            fileList: avatars,
            fields: { entity: "equipment" },
          },
        ],
      },
      {
        items: [
          {
            type: "text",
            label: "Name",
            name: "name",
            required: true,
          },
          {
            type: "text",
            label: "Nickname",
            name: "nickName",
            required: true,
          },
        ],
      },
      {
        items: [
          {
            type: "text",
            label: "Location",
            name: "location",
            required: true,
          },

          {
            type: "number",
            label: "Latitude",
            name: "lat",
            col: 4,
            required: true,
          },

          {
            type: "number",
            label: "Logitude",
            name: "long",
            col: 4,
            required: true,
          },
        ],
      },
      {
        items: [
          {
            type: "async-select",
            label: "Status",
            name: "status",
            required: true,
            options: Object.keys(CompanyEquipmentStatus).map((status) => {
              return {
                value: CompanyEquipmentStatus[status],
                label: status,
              };
            }),
          },
          {
            type: "async-select",
            label: "Equipment Type",
            name: "type",
            required: true,
            options: Object.keys(EquipmentType).map((type) => {
              return {
                value: EquipmentType[type],
                label: type,
              };
            }),
          },
        ],
      },
      {
        items: [
          {
            type: "number",
            label: "Rate",
            name: "rate",
            addonBefore: "AUD",
            addonAfter: "$",
            required: true,
            col: 5,
          },
          {
            type: "async-select",
            label: "Rate By",
            name: "rateBy",
            required: true,
            options: Object.keys(RateBy).map((rateBy) => {
              return {
                value: RateBy[rateBy],
                label: rateBy,
              };
            }),
            col: 3,
          },
        ],
      },
      {
        items: [
          {
            type: "text-area",
            label: "Equipment Description",
            name: "description",
            col: 12,
            maxLen: 200,
          },
        ],
      },
      {
        items: [
          {
            additionalInfo: "Equipment license, insurance, etc. ",
            type: "attachments",
            label: "Attachments (2MB)",
            name: "attachments",
            col: 12,
            maxLen: 200,
            fileList: attachments,
            fields: { entity: "equipment" },
          },
        ],
      },
    ];
  }, [editEquipments]);

  const fetchEquipments = async ({ page, q }) => {
    try {
      setIsLoading(true);
      const { total, results } = await getEquipments({ page, perPage, q });
      setTotal(total);
      setEquipments(results);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("user :: ", user)
    if (!user || ![UserRoles.Admin, UserRoles.User].includes(user.role)) return;
    fetchEquipments({ page, perPage, q: searchQuery });
  }, [user, page, searchQuery]);

  const handleSubmit = async ({ rate, rateBy, ...equipmentData }) => {
    const attachments =
      equipmentData.attachments &&
        equipmentData.attachments.fileList &&
        equipmentData.attachments.fileList.length
        ? equipmentData.attachments.fileList.map((file) => {
          if (!file.response) return file;
          return file.response.file;
        })
        : equipmentData.attachments;

    const avatars =
      equipmentData.avatars &&
        equipmentData.avatars.fileList &&
        equipmentData.avatars.fileList.length
        ? equipmentData.avatars.fileList.map((file) => {
          if (!file.response) return file;
          return file.response.file;
        })
        : equipmentData.avatars;

    const rateData = rate ? { rate, rateBy } : undefined;

    if (editEquipments) {
      await updateEquipment({
        ...equipmentData,
        attachments,
        avatars,
        rate: rateData,
        id: editEquipments.id,
      });
    } else {
      await createEquipment({
        ...equipmentData,
        attachments,
        avatars,
        rate: rateData,
      });
    }
    setEditEquipments(null);
    setShowCreateEquipments(false);
    fetchEquipments({ page, perPage });
  };

  const handleDelete = async (equipment) => {
    await deleteEquipment(equipment.id);
    fetchEquipments(page, perPage);
  };

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Nick Name", dataIndex: "nickName", key: "nickName" },
    { title: "Status", dataIndex: "status", key: "status" },
    { title: "Type", dataIndex: "type", key: "type" },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this equipment?"
            onConfirm={() => handleDelete(equipments[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setEditEquipments(equipments[index])}
          ></Button>
        </Space>
      ),
    },
  ];

  if (user && ![UserRoles.Admin, UserRoles.User].includes(user.role))
    return <NoPermission role={user.role} />;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <PageHeader
          onBack={() => navigate(-1)}
          title="Equipments"
          subTitle="List of equipments in company"
        />

        <Button type="primary" onClick={() => setShowCreateEquipments(true)}>
          Create Equipments
        </Button>
      </Space>

      <Space>
        <Input.Search
          className="search"
          placeholder="Equipment name"
          onSearch={(str) => {
            setSearchQuery(str);
            setPage(1);
          }}
        />
      </Space>

      <Table
        dataSource={equipments.map((equipment, index) => {
          return {
            no: dataIndex + index + 1,
            ...equipment,
            type: Object.keys(EquipmentType).find(
              (type) => EquipmentType[type] === equipment.type
            ),
            status: Object.keys(CompanyEquipmentStatus).find(
              (status) => CompanyEquipmentStatus[status] === equipment.status
            ),
          };
        })}
        columns={TableCols}
        loading={isLoading}
        pagination={{
          size: "small",
          total,
          pageSize: 5,
          current: parseInt(page),
          onChange: setPage,
          showQuickJumper: true,
          showTotal: () => `Total ${total} equipments`,
          position: ["bottomLeft"],
        }}
      />

      {(showCreateEquipments || editEquipments) && (
        <EntityForm
          initialValues={{
            ...editEquipments,
            rate:
              editEquipments &&
                editEquipments.rate &&
                parseFloat(editEquipments.rate.rate)
                ? editEquipments.rate.rate
                : undefined,
            rateBy:
              editEquipments && editEquipments.rate
                ? editEquipments.rate.rateBy
                : undefined,
          }}
          sections={sections}
          title="Equipment"
          open={showCreateEquipments || editEquipments}
          onCancel={() => {
            setShowCreateEquipments(false);
            setEditEquipments(null);
          }}
          onSubmit={handleSubmit}
          onDelete={() => handleDelete(editEquipments).then(() => setEditEquipments(null))}
          isEditMode={!!editEquipments}
        ></EntityForm>
      )}
    </Space>
  );
}
